import { useLayoutEffect, useRef, useState } from 'react';

import { DARK_CLASS, LIGHT_CLASS } from './constants';

/**
 * Carries the ambient colour scheme across a portal.
 *
 * The `.dark` class is the whole colour-scheme API (see `constants.ts`): a DS 2.0 component is dark
 * because it SITS somewhere dark, not because it was told to be. Anything that portals to
 * `document.body` breaks that, because it leaves the React tree's position behind — the popup lands
 * as a child of `<body>`, outside whatever ancestor bounded the dark region, and renders light on a
 * dark page.
 *
 * The fix is to look the answer up where the component actually lives. The caller renders the
 * returned `sentinelRef` on a hidden element at its real place in the tree; when `active` flips, this
 * walks up from there for a `.dark` ancestor and hands back the class to put on the portal. The call
 * site still says nothing about theme, which is the point.
 *
 * `useLayoutEffect` rather than `useEffect` deliberately: the portal's first render happens in the
 * same commit as the sentinel's, so resolving the scheme in a passive effect would paint one frame
 * of a light dialog on a dark page before correcting itself.
 *
 * @param active Whether the portalled content is mounted. Re-resolved every time this turns true, so
 *   a dialog that opens after its surroundings changed theme still gets the current answer.
 * @param from An element to resolve from INSTEAD of the sentinel, for a component that already has a
 *   node at its real place in the tree. An anchored popup is the case: its `anchor` is exactly such
 *   a node, so `Menu` passes it and renders no sentinel at all. Callers that render content in place
 *   (`Dialog`, `Select`) leave this out and use `sentinelRef`.
 */
export const useInheritedDarkMode = (active: boolean, from?: Element | null) => {
  const sentinelRef = useRef<HTMLElement | null>(null);
  const [isDark, setIsDark] = useState(false);

  useLayoutEffect(() => {
    if (!active) {
      return;
    }

    /**
     * The NEAREST marked ancestor wins, which is why this asks for both classes at once rather than
     * just `.dark`.
     *
     * A bare `closest('.dark')` walks straight past a nearer light region and answers "dark" — and a
     * light region inside a dark one is a real shape, seven of them modals over the dark live room.
     * That is what wrapped light MUI selects in a dark `Dialog` surface on `StartNatteringModal`.
     *
     * It cannot be solved in CSS: `:where(.dark) &` and a light counterpart would have equal
     * specificity, so source order would decide globally rather than proximity deciding locally.
     * `closest` with a two-class selector gives proximity for free, because it stops at the first
     * match going up.
     *
     * `closest` walks the DOM tree, not the layout tree, so a `hidden` sentinel answers just as well
     * as a visible one — and costs nothing. Same for an anchor that is scrolled out of view.
     */
    const nearest = (from ?? sentinelRef.current)?.closest(`.${DARK_CLASS}, .${LIGHT_CLASS}`);

    setIsDark(nearest?.classList.contains(DARK_CLASS) ?? false);
  }, [active, from]);

  return { sentinelRef, inheritedDarkClass: isDark ? DARK_CLASS : undefined };
};
